<template>
    <div :style="(minHeight) ? 'min-height:' + minHeight + 'px;' : false">
        <div class="fullwidth-wrapper fullwidth-wrapper--momentum">
            <div
                aria-hidden="true"
                :id="id"
                class="fullwidth-wrapper__content period-chart"
                @mousedown="handleMouseDown"
                @mouseup="handleMouseUp"
                @mousemove="handleMouseMove"
                @mouseleave="handleMouseLeave">
            </div>
            
        </div>
        <div class="transit-btn">
            <ul class="buttons-list buttons-list--row buttons-list--justify">
                <li
                    v-if="iDay > 1"
                    class="buttons-list__item">
                    <svg
                        @click="$emit('prev-year')"
                        width="24"
                        height="24"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="#f586cb"
                        fill-rule="evenodd"
                        cursor="pointer"
                        clip-rule="evenodd"><path d="M20 .755l-14.374 11.245 14.374 11.219-.619.781-15.381-12 15.391-12 .609.755z"/></svg>
                </li>
                <li
                    v-if="iDay < userAge && iDay < maxIday"
                    class="buttons-list__item buttons-list__item--end">
                    <svg
                        @click="$emit('nxt-year')"
                        width="24"
                        height="24"
                        fill="#f586cb"
                        cursor="pointer"
                        xmlns="http://www.w3.org/2000/svg"
                        fill-rule="evenodd"
                        clip-rule="evenodd"><path d="M4 .755l14.374 11.245-14.374 11.219.619.781 15.381-12-15.391-12-.609.755z"/></svg>
                </li>
            </ul>
        </div>
        <div style="margin-top: 120px;"></div>
        <div class="container">
            <ul class="period-legend">
                <li
                    v-for="(period, index) in data"
                    :key="'period-' + index"
                    class="period-legend__item">
                    <img
                        class="period-legend__item-artwork"
                        :class="'color-filter--' + period.year_couleur.toLowerCase()"
                        :src="artworkFlowerUrl"
                        alt="" />
                    <div>
                        <h3 class="title--4">{{ $t(period.year_domaine + "-barometer") }} <span class="title--5">({{ period.lenght }}<span class="sr-only">{{ $t("from_x_to_y", { x: $dayjs(period.datecalendar_first).format('DD/MM/YYYY'), y: $dayjs(period.datecalendar_last).format('DD/MM/YYYY')}) }}</span>)</span></h3>
                        <p>{{ period.desc }}</p>
                    </div>
                </li>
            </ul>
        </div>
    </div>
</template>

<script>
// @ is an alias to /src

import Vue from "vue";

import d3 from "@/utils/d3/d3.js";
import { capitalize } from "@/utils/helpers/string.js";

import dayjs from "dayjs";

import { v4 as uuidv4 } from "uuid";


var isoWeek = require("dayjs/plugin/isoWeek");
dayjs.extend(isoWeek);

export default {
    name: "PeriodChart",
    props: {
        data: {
            type: Array,
            required: true
        },
        minHeight: {
            type: Number,
            required: false,
            default: 0
        },
        iDay: {
            type: Number,
            required: true
        },
        maxIday: {
            type: Number,
            required: true
        },
        userAge: {
            type: Number,
            required: true
        }
    },
    data() {
        return {
            id: "period-chart-" + uuidv4(),
            x: 0,
            y: 0,
            top: 0,
            left: 0,
            isMouseDown: false,
            isDragEnable: false,
            isMobile: true,
        };
    },
    computed: {
        cleanedData() {
            const data = this.data.map((item, index) => {
                let period = [] ;

                const y = index + 1;

                period.push({
                    x: d3.timeParse("%Y%m%d")(item.datecalendar_first),
                    xMiddle: d3.timeParse("%Y%m%d")(item.datemidyear),
                    y: y,
                    desc: item.desc,
                    lenght: item.lenght,
                    color: item.year_couleur,
                    domain: this.$t(item.year_domaine + "-barometer")
                });

                period.push({
                    x: d3.timeParse("%Y%m%d")(item.datecalendar_last),
                    y: y,
                    desc: item.desc,
                    lenght: item.lenght,
                    color: item.year_couleur,
                    domain: this.$t(item.year_domaine + "-barometer")
                });

                return period;
            });

            return data;
        },
        artworkFlowerUrl() {
            return require("@/assets/images/artworks/flower-cycle-plain.svg");
        }
    },
    watch: {
        data() {
            this.resetChart();
            this.buildChart();
        }
    },
    mounted: function() {

        // Responsive Behavior
        /* istanbul ignore next */
        this.$responsive.registerMediaQuery("from-medium", () => {
            this.isMobile = false;
        }, () => {
            this.isMobile = true;
        });

        this.buildChart();
    },
    methods: {
        resetChart() {
            const wrapper = document.getElementById(this.id);

            wrapper.innerHTML = "";
        },
        buildChart() {
            Vue.nextTick(() => {
                const flattenedValue = this.cleanedData.map((domain) => {
                    return domain.map((item) => {
                        return item.y;
                    });
                }).flat();

                const yValueRange = d3.extent(flattenedValue);

                const adaptedYValueRange = [yValueRange[0], yValueRange[1] + 10];

                // set the dimensions and margins of the graph
                let margin = {top: 50, right: 100, bottom: 100, left: 100};

                if (this.isMobile) {
                    margin.left = 0;
                    margin.right = 45;
                }

                const width = 940;
                const height = (85 * this.data.length) - 85 - 85 + margin.top + margin.bottom;

                // append the svg object to the body of the page
                var svg = d3.select("#" + this.id)
                    .append("svg")
                    .attr("preserveAspectRatio", "xMinYMin meet")
                    .attr("width", width + margin.right + margin.left)
                    .attr("height", height + margin.top + margin.bottom)
                    .attr("viewBox", `0 0 ${width + margin.right + margin.left} ${height + margin.top + margin.bottom}`)
                    .append("g")
                    .attr("transform", "translate(" + (margin.left + 45) + ", " + margin.top +")");

                const xRangeStart = dayjs(this.cleanedData[0][0].x).startOf("month");
                const xRangeEnd = dayjs(this.cleanedData[this.cleanedData.length - 1][1].x).add(1, "month").startOf("month");

                let x = d3.scaleTime()
                    .domain([xRangeStart, xRangeEnd])
                    .range([0, width - 72.5]);

                // Add Y axis
                let y = d3.scaleLinear()
                    .domain(adaptedYValueRange)
                    .range([ height, 0]);

                const xAxisRangeType = d3.timeMonth;

                const xAxis = g => g
                    .attr("transform", `translate(0, ${height})`)
                    .attr("class", "x-axis")
                    .call(d3.axisBottom(x)
                        .ticks(xAxisRangeType)
                        .tickFormat((data, index) => {
                            const formatTime = d3.timeFormat("%b %Y");
                            return formatTime(data);
                        })
                        .tickSizeInner(10)
                        .tickSizeOuter(15)
                    )
                    .call(g => g.select(".domain").remove());

                const line = d3.line()
                    .x(d => x(d.x))
                    .curve(d3.curveBasis);

                svg.append("g")
                    .call(xAxis);

                for (var i = 0; i <= this.cleanedData.length - 1; i++) {

                    // Get the last object on index 0 (instead of the first) and so on
                    const currentAdaptedIndex = (this.cleanedData.length - 1) - i;

                    /* Define the data for the circles */
                    var period = svg.append("g")
                        .attr("class", "period");

                    // Add the line
                    period.append("path")
                        .datum(this.cleanedData[i])
                        .attr("fill", "none")
                        .attr("stroke", "#" + this.cleanedData[i][0].color)
                        .attr("stroke-width", 1.5)
                        .attr("stroke-linecap", "round")
                        .attr("stroke-dasharray", "1, 5")
                        .attr("d", d3.line() // Curve the line
                            .x(function(d) {
                                return x(d.x);
                            })
                            .y(function(d) {
                                return (d.y * 85) - 85;
                            })
                        );

                    /* Define the data for the circles */
                    var point = period.append("g")
                        .selectAll("dot")
                        .data(this.cleanedData[i]);

                    /*Create and place the "blocks" containing the circle and the text */
                    var pointGroup = point.enter()
                        .append("g")
                        .attr("class", (d) => {
                            let classValue = "point-wrapper";
                            return classValue;
                        })
                        .attr("transform", function(d) {
                            return "translate("+ x(d.x) + ", " + ((d.y * 85) - 85) + ")";
                        });

                    /*Create the circle for each block */
                    var circle = pointGroup.append("circle")
                        .attr("class", "point")
                        .attr("r", (d) => {
                            let pointRadius = 8;

                            return pointRadius;
                        })
                        .attr("stroke", (d) => {
                            return "#" + d.color;
                        })
                        .attr("stroke-width", 0)
                        .attr("fill", (d) => {
                            return "#" + d.color;
                        });

                    /* Create the text for each block */
                    period.append("text")
                        .attr("class", "period__text")
                        .text(this.cleanedData[i][0].domain + " (" + this.cleanedData[i][0].lenght + ")")
                        .attr("x", x(this.cleanedData[i][0].x))
                        .attr("y", ((this.cleanedData[i][0].y * 85) - 85) - 25)
                        .attr("fill", "#fff")
                        // .attr("text-anchor", "le")
                        .attr("dominant-baseline", "central");
                }

            });
        },
        handleMouseUp() {
            this.isMouseDown = false;
        },
        handleMouseDown() {
            this.isMouseDown = true;
            this.y = event.pageY;
            this.x = event.pageX;
            this.top = this.$el.scrollTop;
            this.left = this.$el.scrollLeft;
        },
        handleMouseMove(event) {
            this.isDragEnable = true;
            if (this.isDragEnable && this.isMouseDown) {
                this.$el.scrollLeft = this.left - event.pageX + this.x;
                this.$el.scrollTop = this.top - event.pageY + this.y;
            }
        },
        handleMouseLeave() {
            this.isDragEnable = false;
        }
    }
};
</script>
<style scoped>
.transit-btn {
    margin-top: -120px;
    margin-left: 50px;
    margin-right: 50px;
}
</style>
